import { useEffect, useState } from 'react';
import Layout from '@/react-app/components/Layout';
import ReservationModal from '@/react-app/components/ReservationModal';
import EditReservationModal from '@/react-app/components/EditReservationModal';
import TimelineCalendar from '@/react-app/components/TimelineCalendar';
import { 
  Calendar, 
  Plus, 
  Search, 
  Filter,
  Eye,
  Edit,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  X,
  Home,
  Users
} from 'lucide-react';
import { ReservationWithDetails, Room } from '@/shared/types';
import DocumentActions from '@/react-app/components/DocumentActions';

interface DayRoomsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: string;
  rooms: Room[];
  reservations: ReservationWithDetails[];
  onRoomSelect: (room: Room) => void;
}

function DayRoomsModal({ isOpen, onClose, selectedDate, rooms, reservations, onRoomSelect }: DayRoomsModalProps) {
  if (!isOpen) return null;

  const getRoomStatus = (roomId: number) => {
    // Only consider room occupied if guest has checked in
    const occupiedReservation = reservations.find(reservation => 
      reservation.room_id === roomId && 
      reservation.status === 'checked_in' &&
      selectedDate >= reservation.check_in_date && 
      selectedDate < reservation.check_out_date
    );
    
    if (occupiedReservation) {
      return { status: 'occupied', reservation: occupiedReservation };
    }
    
    // Check for confirmed reservations (reserved but not checked in)
    const reservedReservation = reservations.find(reservation => 
      reservation.room_id === roomId && 
      reservation.status === 'confirmed' &&
      selectedDate >= reservation.check_in_date && 
      selectedDate < reservation.check_out_date
    );
    
    if (reservedReservation) {
      return { status: 'reserved', reservation: reservedReservation };
    }
    
    return { status: 'available', reservation: null };
  };

  const availableRooms = rooms.filter(room => getRoomStatus(room.id).status === 'available');
  const reservedRooms = rooms.filter(room => getRoomStatus(room.id).status === 'reserved');
  const occupiedRooms = rooms.filter(room => getRoomStatus(room.id).status === 'occupied');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />
        
        <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Quartos para {new Date(selectedDate).toLocaleDateString('pt-BR', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {availableRooms.length} disponíveis • {reservedRooms.length} reservados • {occupiedRooms.length} ocupados
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Available Rooms */}
            <div>
              <div className="flex items-center mb-4">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Quartos Disponíveis ({availableRooms.length})
                </h4>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {availableRooms.map((room) => (
                  <div
                    key={room.id}
                    onClick={() => onRoomSelect(room)}
                    className="p-4 border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-lg cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="font-medium text-gray-900 dark:text-white group-hover:text-green-700 dark:group-hover:text-green-300">
                          Quarto {room.room_number}
                        </h5>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{room.room_type}</p>
                        <div className="flex items-center mt-1 text-xs text-gray-500 dark:text-gray-400">
                          <Users className="w-3 h-3 mr-1" />
                          {room.max_occupancy} pessoas
                        </div>
                      </div>
                      <div className="text-green-600 dark:text-green-400">
                        <Plus className="w-5 h-5" />
                      </div>
                    </div>
                  </div>
                ))}
                {availableRooms.length === 0 && (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    Nenhum quarto disponível
                  </p>
                )}
              </div>
            </div>

            {/* Reserved Rooms */}
            <div>
              <div className="flex items-center mb-4">
                <div className="w-3 h-3 bg-indigo-500 rounded-full mr-2"></div>
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Quartos Reservados ({reservedRooms.length})
                </h4>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {reservedRooms.map((room) => {
                  const { reservation } = getRoomStatus(room.id);
                  return (
                    <div
                      key={room.id}
                      className="p-4 border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-white">
                            Quarto {room.room_number}
                          </h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{room.room_type}</p>
                          {reservation && (
                            <div className="mt-2">
                              <p className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
                                {reservation.guest_name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(reservation.check_in_date).toLocaleDateString('pt-BR')} - {new Date(reservation.check_out_date).toLocaleDateString('pt-BR')}
                              </p>
                              <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                                Aguardando check-in
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="text-indigo-600 dark:text-indigo-400">
                          <Calendar className="w-5 h-5" />
                        </div>
                      </div>
                    </div>
                  );
                })}
                {reservedRooms.length === 0 && (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    Nenhum quarto reservado
                  </p>
                )}
              </div>
            </div>

            {/* Occupied Rooms */}
            <div>
              <div className="flex items-center mb-4">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Quartos Ocupados ({occupiedRooms.length})
                </h4>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {occupiedRooms.map((room) => {
                  const { reservation } = getRoomStatus(room.id);
                  return (
                    <div
                      key={room.id}
                      className="p-4 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded-lg"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-white">
                            Quarto {room.room_number}
                          </h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{room.room_type}</p>
                          {reservation && (
                            <div className="mt-2">
                              <p className="text-sm font-medium text-red-700 dark:text-red-300">
                                {reservation.guest_name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(reservation.check_in_date).toLocaleDateString('pt-BR')} - {new Date(reservation.check_out_date).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="text-red-600 dark:text-red-400">
                          <Home className="w-5 h-5" />
                        </div>
                      </div>
                    </div>
                  );
                })}
                {occupiedRooms.length === 0 && (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    Nenhum quarto ocupado
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Reservations() {
  const [reservations, setReservations] = useState<ReservationWithDetails[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [showEditReservationModal, setShowEditReservationModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<ReservationWithDetails | null>(null);
  const [showDayRoomsModal, setShowDayRoomsModal] = useState(false);
  const [clickedDate, setClickedDate] = useState('');
  const [preSelectedRoom, setPreSelectedRoom] = useState<Room | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reservationsRes, roomsRes] = await Promise.all([
        fetch('/api/reservations'),
        fetch('/api/rooms')
      ]);
      const reservationsData = await reservationsRes.json();
      const roomsData = await roomsRes.json();
      setReservations(reservationsData);
      setRooms(roomsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredReservations = reservations.filter(reservation => {
    const matchesSearch = reservation.guest_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         reservation.room_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || reservation.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'checked_in': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'checked_out': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
      case 'cancelled': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'partial': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'pending': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'refunded': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed': return <Clock className="w-4 h-4" />;
      case 'checked_in': return <CheckCircle className="w-4 h-4" />;
      case 'checked_out': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };


  const handleRoomSelect = (room: Room) => {
    setPreSelectedRoom(room);
    setShowDayRoomsModal(false);
    setShowReservationModal(true);
  };

  // Handler para clique no calendário timeline
  const handleTimelineClick = (date: string, room: Room) => {
    setClickedDate(date);
    setPreSelectedRoom(room);
    setShowReservationModal(true);
  };

  // Handler para clique em reserva no timeline
  const handleReservationClick = (reservation: ReservationWithDetails) => {
    // Aqui você pode abrir um modal de detalhes da reserva ou edição
    console.log('Reserva clicada:', reservation);
  };


  return (
    <Layout>
      <div className="p-6">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reservas</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Gerencie todas as reservas do hotel
              </p>
            </div>
            <button
              onClick={() => {
                setPreSelectedRoom(null);
                setShowReservationModal(true);
              }}
              className="mt-4 sm:mt-0 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-lg flex items-center space-x-2 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Plus className="w-5 h-5" />
              <span>Nova Reserva</span>
            </button>
          </div>
        </div>

        {/* Timeline Calendar View */}
        <div className="mb-8">
          <TimelineCalendar
            rooms={rooms}
            reservations={reservations}
            onDateRoomClick={handleTimelineClick}
            onReservationClick={handleReservationClick}
          />
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar por hóspede ou quarto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
              >
                <option value="all">Todos os Status</option>
                <option value="confirmed">Confirmadas</option>
                <option value="checked_in">Check-in Feito</option>
                <option value="checked_out">Check-out Feito</option>
                <option value="cancelled">Canceladas</option>
              </select>
            </div>
          </div>
        </div>

        {/* Reservations List */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Lista de Reservas ({filteredReservations.length})
            </h2>
          </div>
          
          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Carregando reservas...</p>
            </div>
          ) : filteredReservations.length === 0 ? (
            <div className="p-6 text-center">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Nenhuma reserva encontrada</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredReservations.map((reservation) => (
                <div key={reservation.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                            {reservation.guest_name}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Quarto {reservation.room_number} • {reservation.room_type}
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Check-in</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {new Date(reservation.check_in_date).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Check-out</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {new Date(reservation.check_out_date).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Valor Total</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                            <DollarSign className="w-4 h-4 mr-1" />
                            {new Intl.NumberFormat('pt-BR', { 
                              style: 'currency', 
                              currency: 'BRL' 
                            }).format(reservation.total_amount)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Hóspedes</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {reservation.adults + reservation.children} pessoas
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-4 flex items-center space-x-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(reservation.status)}`}>
                          {getStatusIcon(reservation.status)}
                          <span className="ml-1 capitalize">{reservation.status.replace('_', '-')}</span>
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(reservation.payment_status)}`}>
                          <DollarSign className="w-3 h-3 mr-1" />
                          <span className="capitalize">{reservation.payment_status}</span>
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                        <Eye className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedReservation(reservation);
                          setShowEditReservationModal(true);
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <DocumentActions 
                        reservationId={reservation.id} 
                        type="voucher" 
                        title="Voucher" 
                        color="text-blue-500 hover:text-blue-700" 
                      />
                      <DocumentActions 
                        reservationId={reservation.id} 
                        type="guest-form" 
                        title="Ficha do Hóspede" 
                        color="text-green-500 hover:text-green-700" 
                      />
                      <DocumentActions 
                        reservationId={reservation.id} 
                        type="invoice" 
                        title="Fatura" 
                        color="text-purple-500 hover:text-purple-700" 
                      />
                      <DocumentActions 
                        reservationId={reservation.id} 
                        type="receipt" 
                        title="Recibo" 
                        color="text-orange-500 hover:text-orange-700" 
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reservation Modal */}
        <ReservationModal
          isOpen={showReservationModal}
          onClose={() => {
            setShowReservationModal(false);
            setPreSelectedRoom(null);
            setClickedDate('');
          }}
          onRefresh={fetchData}
          preSelectedRoom={preSelectedRoom}
          preSelectedDate={clickedDate}
        />

        {/* Day Rooms Modal */}
        <DayRoomsModal
          isOpen={showDayRoomsModal}
          onClose={() => setShowDayRoomsModal(false)}
          selectedDate={clickedDate}
          rooms={rooms}
          reservations={reservations}
          onRoomSelect={handleRoomSelect}
        />

        {/* Edit Reservation Modal */}
        <EditReservationModal
          reservation={selectedReservation}
          isOpen={showEditReservationModal}
          onClose={() => {
            setShowEditReservationModal(false);
            setSelectedReservation(null);
          }}
          onRefresh={fetchData}
        />
      </div>
    </Layout>
  );
}
